import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';

const LinkingAttemptsContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const LINKING_ATTEMPT_LOADING_STARTED = 'linking-attempt-started';
const LINKING_ATTEMPT_LOADING_SUCCEEDED = 'linking-attempt-succeeded';
const LINKING_ATTEMPT_LOADING_FAILED = 'linking-attempt-loading-failed';

const LINKING_ATTEMPT_SET_COLLECTION = 'linking-attempt-set-collection';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    linkingAttemptCollection: null,
    isLinkingAttemptRefreshing: false,
    linkingAttemptRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        linkingAttemptCollection: action.linkingAttemptCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case LINKING_ATTEMPT_LOADING_STARTED: {
      return {
        ...state,
        isLinkingAttemptRefreshing: true,
        linkingAttemptRefreshError: null,
      };
    }
    case LINKING_ATTEMPT_LOADING_SUCCEEDED: {
      return {
        ...state,
        isLinkingAttemptRefreshing: false,
        linkingAttemptRefreshError: null,
        linkingAttemptCollection: action.linkingAttemptCollection,
      };
    }
    case LINKING_ATTEMPT_LOADING_FAILED: {
      return {
        ...state,
        isLinkingAttemptRefreshing: false,
        linkingAttemptRefreshError: action.linkingAttemptRefreshError,
      };
    }
    case LINKING_ATTEMPT_SET_COLLECTION: {
      return {
        ...state,
        linkingAttemptCollection: action.collection,
      };
    }
    default: {
      throw new Error(`Unhandled LinkingAttemptsContext action type: ${action.type}`);
    }
  }
};

const LinkingAttemptsProvider = ({
  children,
  agentResource,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const loadLinkingAttemptsCollection = useCallback(async () => {
    try {
      dispatch({
        type: LINKING_ATTEMPT_LOADING_STARTED,
      });

      const linkingAttemptsCollection = await agentResource.relationship(fr.relationships.linkingAttempts.name).link('related').get();

      dispatch({
        type: LINKING_ATTEMPT_SET_COLLECTION,
        collection: linkingAttemptsCollection,
      });
    } catch (err) {
      dispatch({
        type: LINKING_ATTEMPT_LOADING_FAILED,
        error: err,
      });
    }
  }, [agentResource]);

  const refreshLinkingAttemptCollection = React.useCallback(async () => {
    const { linkingAttemptCollection: linkingAttemptColl } = state;

    if (linkingAttemptColl === null) {
      return;
    }

    if (linkingAttemptColl.resources().length() === 0) {
      return;
    }

    const [linkingAttemptRes] = linkingAttemptColl;

    try {
      dispatch({ type: LINKING_ATTEMPT_LOADING_STARTED });

      const coll = await linkingAttemptRes.link('self').get();

      dispatch({ type: LINKING_ATTEMPT_LOADING_SUCCEEDED, linkingAttemptCollection: coll });
    } catch (err) {
      dispatch({ type: LINKING_ATTEMPT_LOADING_FAILED, linkingAttemptRefreshError: err });
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      linkingAttemptCollection: state.linkingAttemptCollection,
      isLinkingAttemptRefreshing: state.isLinkingAttemptRefreshing,
      linkingAttemptRefreshError: state.linkingAttemptRefreshError,
      refreshLinkingAttemptCollection,
      loadLinkingAttemptsCollection,
    };
  }, [
    state,
    refreshLinkingAttemptCollection,
  ]);

  useEffect(() => {
    if (!agentResource?.relationship(fr.relationships.linkingAttempts.name)) {
      return;
    }
    loadLinkingAttemptsCollection();
  }, [agentResource]);

  return (
    <LinkingAttemptsContext.Provider value={memoizedValue}>
      {children}
    </LinkingAttemptsContext.Provider>
  );
};

// Use LinkingAttempts State Context
const useLinkingAttemptsContext = () => {
  const context = React.useContext(LinkingAttemptsContext);
  if (context === undefined) {
    throw new Error('useLinkingAttemptsContext must be used within a LinkingAttemptsProvider');
  }
  return context;
};

LinkingAttemptsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  agentResource: PropTypes.object.isRequired,
};

export {
  LinkingAttemptsProvider,
  useLinkingAttemptsContext,
};
